import type { InvestmentStatus } from "@eu.mdotm.api/sphere";
import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import { runWithErrorReporting } from "$root/api/error-reporting/report";
import type { EntityManagementActions } from "$root/components/spawnable/entity-management/actions";
import { spawnDeleteDialog } from "$root/components/spawnable/entity-management/delete-dialog";
import { spawnDuplicateDialog } from "$root/components/spawnable/entity-management/duplicate-dialog";
import { spawnRenameDialog } from "$root/components/spawnable/entity-management/rename-dialog";
import { platformToast } from "$root/notification-system/toast";
import { ToastableError } from "$root/utils";
import { t } from "i18next";
import { useMemo } from "react";
import { aclByArea } from "../acl/checkers/all";
import { useUserValue } from "../user";
import { portfolioRepository } from "./repository";
import { referenceRepository } from "./references-repository";

export function usePortfolioEntityManagementActions(
	portfolio:
		| {
				name?: string;
				uuid?: string;
				richAcl?: { acl?: RichAccessControl[] };
				reference?: boolean;
				linkedPortfolios?: number;
				status?: InvestmentStatus;
				nofUsagesAsNested?: number;
		  }
		| null
		| undefined,
	callbacks?: {
		onRename?(uuid: string): void;
		onDelete?(uuid: string): void;
		onDuplicate?(uuid: string): void;
	},
): Partial<Omit<EntityManagementActions, "createAsync">> {
	const user = useUserValue();
	return useMemo(
		() => (!portfolio ? {} : portfolioEntityManagementActions(user, portfolio, callbacks)),
		[callbacks, portfolio, user],
	);
}

const investmentEntityManagementRepository = {
	delete: portfolioRepository.delete,
	duplicate: portfolioRepository.duplicate,
	rename: portfolioRepository.rename,
	isNameAvailable: portfolioRepository.isInvestmentNameAvailable,
};

const targetPortfolioEntityManagementRepository = {
	delete: referenceRepository.deleteReference,
	duplicate: referenceRepository.duplicateReference,
	rename: referenceRepository.renameReference,
	isNameAvailable: referenceRepository.isReferenceNameAvailable,
};
export function portfolioEntityManagementActions(
	user: { id: string },
	portfolio: {
		name?: string;
		uuid?: string;
		richAcl?: { acl?: RichAccessControl[] };
		reference?: boolean;
		linkedPortfolios?: number;
		status?: InvestmentStatus;
		nofUsagesAsNested?: number;
	},
	callbacks?: {
		onRename?(uuid: string): void;
		onDelete?(uuid: string): void;
		onDuplicate?(uuid: string): void;
	},
	repositoryType: "portfolio" | "targetPortfolio" = "portfolio",
): Partial<Omit<EntityManagementActions, "createAsync">> {
	const repository =
		repositoryType === "portfolio" ? investmentEntityManagementRepository : targetPortfolioEntityManagementRepository;

	return {
		deleteAsync:
			!aclByArea.portfolio.canDelete(user.id, portfolio?.richAcl?.acl ?? []) ||
			(portfolio.reference && (portfolio.linkedPortfolios ?? 0) > 0) ||
			(portfolio.nofUsagesAsNested ?? 0) > 0
				? undefined
				: () =>
						spawnDeleteDialog({
							entityName: portfolio?.name ?? "",
							entityType: "portfolio",
							yesButton: "Proceed",
							noButton: "Cancel",
							async onDeleteAsync() {
								await runWithErrorReporting(
									async () => {
										try {
											await repository.delete(portfolio.uuid ?? "");

											platformToast({
												children: t("PORTFOLIOS.DELETE_OK_MESSAGE", { name: portfolio.name }),
												severity: "success",
												icon: "Portfolio",
											});
											callbacks?.onDelete?.(portfolio.uuid ?? "");
										} catch (err) {
											throw new ToastableError(t("SOMETHING_WENT_WRONG"), { cause: err, icon: "Portfolio" });
										}
									},
									{
										area: "portfolio",
										attemptedOperation: {
											message: `delete ${portfolio.reference ? "reference " : ""}investment "${portfolio.uuid}"`,
											payload: JSON.stringify(portfolio),
										},
									},
								);
							},
						}),
		duplicateAsync:
			!portfolio.status || invalidStatus.includes(portfolio.status)
				? undefined
				: () =>
						spawnDuplicateDialog({
							entityType: "portfolio",
							originalName: portfolio?.name ?? "",
							checkIfNameIsAvailable: repository.isNameAvailable,
							onSubmitAsync(name) {
								return runWithErrorReporting(
									async () => {
										try {
											const { uuid } = await repository.duplicate(portfolio.uuid ?? "", name);

											platformToast({
												children: t("PORTFOLIOS.DUPLICATE_OK_MESSAGE", { name }),
												severity: "success",
												icon: "Portfolio",
											});
											callbacks?.onDuplicate?.(uuid!);

											return uuid!;
										} catch (err) {
											throw new ToastableError(t("SOMETHING_WENT_WRONG"), { cause: err, icon: "Portfolio" });
										}
									},
									{
										area: "portfolio",
										attemptedOperation: {
											message: `duplicate ${portfolio.reference ? "reference " : ""}investment "${portfolio.uuid}"`,
											payload: JSON.stringify(portfolio),
										},
									},
								);
							},
						}),
		renameAsync: !aclByArea.portfolio.canRename(user.id, portfolio?.richAcl?.acl ?? [])
			? undefined
			: () =>
					spawnRenameDialog({
						entityType: "portfolio",
						currentName: portfolio?.name ?? "",
						checkIfNameIsAvailable: repository.isNameAvailable,
						onSubmitAsync(name) {
							return runWithErrorReporting(
								async () => {
									try {
										await repository.rename(portfolio.uuid ?? "", name);

										platformToast({
											children: t("PORTFOLIOS.RENAME_OK_MESSAGE", { name }),
											severity: "success",
											icon: "Portfolio",
										});
										callbacks?.onRename?.(name);

										return name;
									} catch (err) {
										throw new ToastableError(t("SOMETHING_WENT_WRONG"), { cause: err, icon: "Portfolio" });
									}
								},
								{
									area: "portfolio",
									attemptedOperation: {
										message: `rename ${portfolio.reference ? "reference " : ""}investment "${portfolio.uuid}"`,
										payload: JSON.stringify(portfolio),
									},
								},
							);
						},
					}),
	};
}

const invalidStatus: Array<InvestmentStatus> = [
	"ERROR",
	"REVIEW",
	"CALCULATING",
	"RETRIEVING_DATA",
	"PROPOSAL_READY",
	"DRAFT",
];
